import React, { useEffect, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { ARRAY_ERROR } from 'final-form';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, intlShape, injectIntl } from '../../../../util/reactIntl';
import { CATEGORY_ACCESSORY, DAMAGE, RECEIPT, TAGGED, propTypes } from '../../../../util/types';
import { isUploadImageOverLimitError } from '../../../../util/errors';

// Import shared components
import { Button, Form, AspectRatioWrapper, IconCollection, Modal, FieldRadioButton, ResponsiveImage, FieldTextInput } from '../../../../components';

// Import modules from this directory
import ListingImage from './ListingImage';
import css from './EditListingPhotosForm.module.css';
import { getFileExtension } from '../../../../util/dataExtractor';
import { required } from '../../../../util/validators';

const ACCEPT_IMAGES = '.jpg, .jpeg, .png, .heic, .webp';
const ACCEPT_VIDEO = '.mov, .mp4';
let imageCounter = 1; // Initialize the counter

const ENV_TYPE = process.env.REACT_APP_ENV
const DEV_MODE = ["development"];

const UploadField = ({
  type,
  uploadImagesInProgress,
  uploadOtherImages,
  form,
  onImageUploader,
  labelContent,
  intl,
  handleRemoveClick
}) => {
  const image = uploadOtherImages.find((e) => e.imageType === type);

  if (uploadImagesInProgress === type) {
    return <div><div className={css.fallbackWhileDownloading}>
      <span className={css.loader}></span>
      <span className={css.loading}>loading</span>
    </div>
    </div>;
  }

  if (image?.url) {
    return (
      <div className={css.imageUploadWrapper}>
        <button className={css.removeIcon} type="button" onClick={() => {
          handleRemoveClick(image)
        }}><IconCollection icon="crossIcon" /></button>
        <ResponsiveImage
          className={css.rootForImage}
          renderCloudinaryImage={{
            secure_url: image.url,
            alt: `${type} Listing Image`,
            variantsToRender: [
              'listing-card-6x',
              'listing-card-4x',
              'listing-card-2x',
              'listing-card',
            ],
            defaultVariant: 'listing-card-6x',
          }}
        />
      </div>
    );
  }
  const validation = [DAMAGE, TAGGED].includes(type) ? { vaildate: required(intl.formatMessage({ id: "EditListingPhotosForm.required" })) } : {};

  return (
    <FieldTextInput
      id={`add${type}`}
      name={`${type}Images`}
      type="file"
      className={css.addInputfield}
      label={labelContent}
      accept={ACCEPT_IMAGES}
      onChange={(event) => onImageUploader(event.target.files[0], form, type)}
    // {...validation}
    />
  );
};

const ImageUploadError = props => {
  return props.uploadOverLimit ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadOverLimit" />
    </p>
  ) : props.uploadImageError ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadFailed" />
    </p>
  ) : null;
};

// NOTE: PublishListingError and ShowListingsError are here since Photos panel is the last visible panel
// before creating a new listing. If that order is changed, these should be changed too.
// Create and show listing errors are shown above submit button
const PublishListingError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.publishListingFailed" />
    </p>
  ) : null;
};

const ShowListingsError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.showListingFailed" />
    </p>
  ) : null;
};

// Field component that uses file-input to allow user to select images.
export const FieldAddImage = props => {
  const {
    formApi,
    loader,
    onImageUploadHandler,
    aspectWidth = 1,
    aspectHeight = 1,
    values,
    ...rest
  } = props;
  const [imgUrl, setImgUrl] = useState('');
  return (
    <Field form={null} {...rest}>
      {fieldprops => {
        const { accept, input, label, disabled: fieldDisabled } = fieldprops;
        const { name, type } = input;
        const onChange = e => {
          const files = e.target.files;
          const fileArray = Array.from(files);

          // Clear the previous image URL
          setImgUrl('');

          // Iterate over each file in the array
          fileArray.forEach(file => {
            const blobUrl = window.URL.createObjectURL(file);
            setImgUrl(blobUrl);
            formApi.change(`addImage`, file);
            formApi.blur(`addImage`);
            // Call onImageUploadHandler for each file
          });

          onImageUploadHandler(fileArray, formApi, values);
        };

        const inputProps = { accept, id: name, name, onChange, type };
        return (
          <div className={css.addImageWrapper}>
            <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
              {fieldDisabled ? null : (
                <input {...inputProps} multiple className={css.addImageInput} />
              )}
              <label htmlFor={name} className={css.addImage}>
                {loader && imgUrl ? (
                  <img src={imgUrl} />
                ) : (
                  <div className={css.uplaodTxt}>
                    <IconCollection icon="uploadIcon" />
                    {label}
                  </div>
                )}
                {loader ? (
                  <div className={css.fallbackWhileDownloading}>
                    <span className={css.loader}></span>
                    <span className={css.loading}>uploading</span>
                  </div>
                ) : null}
              </label>
            </AspectRatioWrapper>
          </div>
        );
      }}
    </Field>
  );
};
// Component that shows listing images from "images" field array
const FieldListingImage = props => {
  const { name, intl, onRemoveImage, aspectWidth, aspectHeight, variantPrefix } = props;
  return (
    <Field name={name}>
      {fieldProps => {
        const { input } = fieldProps;
        const image = input.value;
        return image ? (
          <ListingImage
            image={image}
            key={image?.id?.uuid || image?.id}
            className={css.thumbnail}
            savedImageAltText={intl.formatMessage({
              id: 'EditListingPhotosForm.savedImageAltText',
            })}
            onRemoveImage={() => onRemoveImage(image?.id)}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
          />
        ) : null;
      }}
    </Field>
  );
};

export const EditListingPhotosFormComponent = props => {
  const [state, setState] = useState({ imageUploadRequested: false });
  const [submittedImages, setSubmittedImages] = useState([]);
  const [uploadImageInProgress, setUploadImageInProgress] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(null);
  const onImageUploadHandler = (files, formApi, values) => {
    const { listingImageConfig, onImageUpload, onUploadImagesToCloudinary, listingId, uploadedVideo, setUploadImages, uploadImages } = props;
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!files || files.length === 0) return;

    setState({ imageUploadRequested: true });
    setUploadImageInProgress(true);

    const modifyCloudinaryUrl = (url, transformations) => {
      const parts = url.split('/upload/');
      return `${parts[0]}/upload/${transformations}/${parts[1]}`;
    };

    const uploadPromises = files.slice(0, (10 - uploadImages.length)).map((file) => {
      // Check file size
      if (file.size > maxSize) {
        alert('File size exceeds the maximum allowed (10 MB)');
        return Promise.resolve(null);
      }

      // Validate file extension
      const extension = getFileExtension(file.name).toLowerCase();

      if (['heic', 'webp'].includes(extension)) {
        // Generate a unique imgId based on the counter
        const imgId = `${listingId}-${imageCounter++}`;
        return onUploadImagesToCloudinary(file, imgId)
          .then((cloudinaryResponse) => {
            if (cloudinaryResponse) {
              return {
                url: modifyCloudinaryUrl(cloudinaryResponse?.secure_url, 'f_auto,q_auto'),
                type: file.type,
                fileName: file.name,
                public_id: cloudinaryResponse.public_id,
                resource_type: cloudinaryResponse.resource_type,
              };
            }
            return null;
          })
          .catch((error) => {
            console.error('Cloudinary upload failed:', error);
            return null;
          });
      }

      return onImageUpload({ id: `${file.name}_${Date.now()}`, file }, listingImageConfig)
        .then((imgId) => {
          if (imgId) {
            return onUploadImagesToCloudinary(file, imgId)
              .then((cloudinaryResponse) => {
                if (cloudinaryResponse) {
                  return {
                    url: cloudinaryResponse?.secure_url,
                    type: file.type,
                    fileName: file.name,
                    public_id: cloudinaryResponse.public_id,
                    resource_type: cloudinaryResponse.resource_type,
                  };
                }
                return null;
              })
              .catch((error) => {
                console.error('Cloudinary upload failed:', error);
                return null;
              });
          }
          return null;
        })
        .catch((error) => {
          console.error('Image upload failed:', error);
          return null;
        });
    });

    Promise.all(uploadPromises)
      .then((uploadResults) => {
        const validUploads = uploadResults.filter((upload) => upload !== null);
        if (validUploads.length > 0) {
          setUploadImages((prevImages) => [...prevImages, ...validUploads]);
          formApi.change("heroImg", values?.heroImg ? values?.heroImg : [...uploadImages, ...validUploads][0]?.public_id);
        }
      })
      .catch((error) => {
        console.error('Image upload process encountered an error:', error);
      })
      .finally(() => {
        setUploadImageInProgress(false);
        setState({ imageUploadRequested: false });
      });
  };

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          form,
          className,
          fetchErrors,
          handleSubmit,
          intl,
          invalid,
          disabled,
          ready,
          saveActionMsg,
          updated,
          updateInProgress,
          touched,
          errors,
          values,
          listingImageConfig,
          listingId,
          onUploadVideosToCloudinary,
          uploadVideoInProgress,
          uploadedVideo,
          onDeleteFromCloudinary,
          uploadImagesInProgress,
          onUploadImagesToCloudinary,
          onSaveDraft,
          history,
          setSaveAsDraft,
          saveAsDraft,
          anyDamage,
          heroImageId,
          setDeleteImageIds,
          savedListingVideo,
          productType,
          uploadOtherImages,
          setUploadOtherImages,
          uploadImages,
          setUploadImages,
          setUploadVideo,
          uploadVideo,
          setIsVideoDeleted,
          mainProductType
        } = formRenderProps;

        const { images, listingImagesUrl } = values || {};

        const { aspectWidth = 1, aspectHeight = 1 } = listingImageConfig;

        const { publishListingError, showListingsError, updateListingError, uploadImageError } =
          fetchErrors || {};
        const uploadOverLimit = isUploadImageOverLimitError(uploadImageError);

        // imgs can contain added images (with temp ids) and submitted images with uniq ids.
        const arrayOfImgIds = imgs => imgs.map(i => (typeof i.id === 'string' ? i.imageId : i.id));
        const imageIdsFromProps = arrayOfImgIds(images);
        const imageIdsFromPreviousSubmit = arrayOfImgIds(submittedImages);
        const imageArrayHasSameImages = isEqual(imageIdsFromProps, imageIdsFromPreviousSubmit);
        const submittedOnce = submittedImages.length > 0;
        const pristineSinceLastSubmit = submittedOnce && imageArrayHasSameImages;

        const submitReady = (updated && pristineSinceLastSubmit) || ready;
        const submitInProgress = updateInProgress && !saveAsDraft;
        const disableCheck = anyDamage?.trim() ? uploadOtherImages?.filter((e) => e.imageType == DAMAGE).length == 0 : null;
        const submitDisabled =
          invalid ||
          disabled ||
          submitInProgress ||
          uploadVideoInProgress ||
          uploadImagesInProgress ||
          state.imageUploadRequested ||
          ready || disableCheck;

        const imagesError = touched?.images && errors?.images && errors?.images[ARRAY_ERROR];

        const classes = classNames(css.root, className);

        useEffect(() => {
          if (uploadImages.length > 0 && !values.heroImg) {
            form.change('heroImg', uploadImages[0].public_id);
          }
        }, [uploadImages.length > 0]);

        const handleDeleteImage = val => {
          setUploadImageInProgress(true);

          const isOtherImage = val.imageType;
          const imageList = isOtherImage ? uploadOtherImages : uploadImages;
          const setImageList = isOtherImage ? setUploadOtherImages : setUploadImages;
          const formKey = isOtherImage ? 'otherImages' : 'listingImagesUrl';

          const updatedImages = imageList.filter(img => img.url !== val.url);

          setImageList(updatedImages);
          form.change(formKey, updatedImages);

          setDeleteImageIds(prevIds => [...prevIds, val.public_id]); // Functional update

          if (val.public_id === values?.heroImg) {
            form.change('heroImg', null);
          }

          setUploadImageInProgress(false);
          setIsDeleteModalOpen(null);
        };

        const deleteImageModal = (
          <Modal
            id="imagesDeleteModal"
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(null);
            }}
            usePortal
            contentClassName={css.modalContent}
            onManageDisableScrolling={() => { }}
            className={css.modalDelete}
          >
            <div className={css.deleteModalContent}>
              <h3>
                <FormattedMessage id="EditlistingPhotosForm.deleteHeading" />
              </h3>
              <p>
                <FormattedMessage id="DraftListingsCard.deleteText" />
              </p>
              <div className={css.modalButton}>
                <Button className={css.cancelBtn} onClick={() => setIsDeleteModalOpen(null)}>
                  <FormattedMessage id="DraftListingsCard.cancel" />
                </Button>
                <Button
                  onClick={() => {
                    handleDeleteImage(isDeleteModalOpen);
                  }}
                  inProgress={false}
                >
                  <FormattedMessage id="DraftListingsCard.delete" />
                </Button>
              </div>
            </div>
          </Modal>
        );

        const handleRemoveClick = image => {
          setIsDeleteModalOpen(image);
        };

        const onVideoUpload = (file, form) => {
          const maxSize = 70 * 1024 * 1024; // 70MB
          if (!file) return;

          if (file.size > maxSize) {
            alert('File size exceeds the maximum allowed (70 MB)');
            return Promise.resolve(null);
          }

          const imgId = `${listingId}-video-${imageCounter++}`;

          return onUploadVideosToCloudinary(file, imgId)
            .then((cloudinaryResponse) => {
              if (cloudinaryResponse) {
                const data = {
                  url: cloudinaryResponse?.secure_url,
                  type: file.type,
                  fileName: file.name,
                  public_id: cloudinaryResponse.public_id,
                  resource_type: cloudinaryResponse.resource_type,
                };

                if (data?.url) {
                  setUploadVideo(data); // this updates state but async

                  // ✅ use the freshly uploaded video `data` directly
                  // form.change('listingVideo', data);
                  // form.change('listingImagesUrl', uploadImages); // also consider using a fresh copy if needed
                  // form.change("otherImages", uploadOtherImages);

                  return data;
                }
              }
              return null;
            })
            .catch((error) => {
              console.error('Cloudinary upload failed:', error);
              return null;
            });
        };


        const onImageUploader = (file, form, type) => {
          const maxSize = 10 * 1024 * 1024; // 10MB
          if (!file) return;

          const modifyCloudinaryUrl = (url, transformations) => {
            const parts = url.split('/upload/');
            return `${parts[0]}/upload/${transformations}/${parts[1]}`;
          };

          if (file.size > maxSize) {
            alert('File size exceeds the maximum allowed (10 MB)');
            return Promise.resolve(null);
          }

          const uploadToCloudinary = (file, imgId) => {
            return onUploadImagesToCloudinary(file, imgId, type)
              .then((cloudinaryResponse) => {
                if (cloudinaryResponse) {
                  const data = {
                    url: modifyCloudinaryUrl(cloudinaryResponse.secure_url, 'f_auto,q_auto'),
                    type: file.type,
                    fileName: file.name,
                    public_id: cloudinaryResponse.public_id,
                    resource_type: cloudinaryResponse.resource_type,
                    imageType: type,
                  };

                  setUploadOtherImages((pre) => [...pre, data]);

                  // Updated form changes:
                  const newOtherImages = [...uploadOtherImages, data];

                  // form.change("otherImages", newOtherImages);
                  // form.change("listingImagesUrl", uploadImages);
                  // form.change('listingVideo', uploadVideo);

                  // Only set heroImg if not already set
                  if (!values?.heroImg && uploadImages.length > 0) {
                    form.change("heroImg", uploadImages[0]?.public_id);
                  }

                  return data;
                }
                return null;
              })
              .catch((error) => {
                console.error('Cloudinary upload failed:', error);
                return null;
              });
          };

          const imgId = `${Date.now()}-${type}`;
          return uploadToCloudinary(file, imgId);
        };

        return (
          <Form
            className={classes}
            onSubmit={e => {
              setSubmittedImages(images);
              handleSubmit(e);
            }}
          >
            {updateListingError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingPhotosForm.updateFailed" />
              </p>
            ) : null}
            {deleteImageModal}
            <label className={css.sectionHeading}><FormattedMessage id="EditListingPhotosForm.photos1" values={{ required: <span className={css.requiredText}>*Required</span> }} /></label>
            <div className={css.imagesFieldArray}>
              {uploadImages.length > 0 ?
                uploadImages.map((image, index) => {
                  return (
                    <div className={css.aspectRoot} key={image?.url}>
                      <ResponsiveImage
                        className={css.rootForImage}
                        renderCloudinaryImage={{
                          secure_url: image?.url,
                          alt: `Listing Image`,
                          variantsToRender: [
                            'listing-card-6x',
                            'listing-card-4x',
                            'listing-card-2x',
                            'listing-card',
                          ],
                          defaultVariant: 'listing-card-6x',
                        }}
                      />
                      <div className={css.closeBtn}>
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveClick(image);
                            if (values.heroImg === image.public_id) {
                              form.change('heroImg', null)
                            }
                          }}
                        >
                          <IconCollection icon="crossIcon" />
                        </button>
                      </div>
                      <FieldRadioButton
                        className={css.heroImageCheckbox}
                        id={image.public_id}
                        name="heroImg"
                        label={intl.formatMessage({ id: "EditListingPhotosForm.mainPhoto" })}
                        value={image.public_id}
                        checked={values.heroImg === image.public_id}
                      />
                    </div>
                  );
                }) : null}
              {
                (uploadImages.length < 10) ?
                  <FieldAddImage
                    id="addImage"
                    name="addImage"
                    accept={ACCEPT_IMAGES}
                    label={
                      <span className={css.chooseImageText}>
                        <span className={css.chooseImage}>
                          <FormattedMessage id="EditListingPhotosForm.chooseImage1" />
                        </span>
                        <span className={css.imageTypes}>
                          <FormattedMessage id="EditListingPhotosForm.imageTypes1" />
                        </span>
                      </span>
                    }
                    type="file"
                    disabled={state.imageUploadRequested}
                    formApi={form}
                    onImageUploadHandler={onImageUploadHandler}
                    aspectWidth={aspectWidth}
                    aspectHeight={aspectHeight}
                    loader={uploadImageInProgress}
                    values={values}
                  />
                  : null
              }

            </div>

            {imagesError ? <div className={css.arrayError}>{imagesError}</div> : null}
            <div className={css.gridSection}>
              <div>
                <label className={css.sectionHeading}><FormattedMessage id="EditListingPhotosForm.videoHeading1" values={{ optional: <span className={css.requiredText}>*Optional</span> }} /></label>
                {uploadVideoInProgress ? <div>
                  <div className={css.fallbackWhileDownloading}>
                    <span className={css.loader}></span>
                    <span className={css.loading}>loading</span>
                  </div>
                </div> : ((uploadVideo)?.url) ? <div className={css.videoWrapper}>
                  <button className={css.removeIcon} type="button"
                    onClick={() => {

                      if (!savedListingVideo) {
                        onDeleteFromCloudinary(uploadVideo); // Delete from Cloudinary if present
                      }

                      setUploadVideo(null); // Clear local state
                      setIsVideoDeleted(true)
                    }}

                  ><IconCollection icon="crossIcon" /></button>
                  <video controls src={(uploadVideo)?.url}>
                  </video>
                </div> : <FieldTextInput
                  id="addVideo"
                  name="addVideo"
                  type="file"
                  className={css.addVideoinput}
                  label={
                    <span className={css.chooseImageText}>
                      <IconCollection icon="videoIcon" />
                      <span className={css.chooseImage}>
                        <FormattedMessage id="EditListingPhotosForm.chooseVideoFormat" />
                      </span>
                      <span className={css.imageTypes}>
                        <FormattedMessage id="EditListingPhotosForm.chooseVideo1" />
                      </span>
                    </span>
                  }
                  accept={ACCEPT_VIDEO}
                  onChange={(event) => onVideoUpload(event.target.files[0], form)}
                  disabled={uploadVideoInProgress}
                />}
              </div>
              <div>
                <label className={css.sectionHeading}><FormattedMessage id="EditListingPhotosForm.tagHeading1" values={{ optional: <span className={css.requiredText}>*Optional</span> }} /></label>
                <UploadField
                  type={TAGGED}
                  uploadImagesInProgress={uploadImagesInProgress}
                  uploadOtherImages={uploadOtherImages}
                  form={form}
                  onImageUploader={onImageUploader}
                  labelContent={
                    <span className={css.chooseImageText}>
                      <IconCollection icon="tagIcon" />
                      <span className={css.chooseImage}>
                        <FormattedMessage id="EditListingPhotosForm.chooseImage1" />
                      </span>
                      <span className={css.imageTypes}>
                        <FormattedMessage id="EditListingPhotosForm.imageTypes1" />
                      </span>
                    </span>
                  }
                  intl={intl}
                  handleRemoveClick={handleRemoveClick}
                />
              </div>
              <div>
                <label className={css.sectionHeading}><FormattedMessage id="EditListingPhotosForm.receiptLabel1" values={{ optional: <span className={css.requiredText}>*Optional</span> }} /></label>
                <UploadField
                  type={RECEIPT}
                  uploadImagesInProgress={uploadImagesInProgress}
                  uploadOtherImages={uploadOtherImages}
                  form={form}
                  onImageUploader={onImageUploader}
                  labelContent={
                    <span className={css.chooseImageText}>
                      <IconCollection icon="recieptIcon" />
                      <span className={css.chooseImage}>
                        <FormattedMessage id="EditListingPhotosForm.chooseImage1" />
                      </span>
                      <span className={css.imageTypes}>
                        <FormattedMessage id="EditListingPhotosForm.imageTypes1" />
                      </span>
                    </span>
                  }
                  intl={intl}
                  handleRemoveClick={handleRemoveClick}
                />
              </div>
              {
                (anyDamage?.trim()) ? (<div className={css.damageSection}>
                  <label className={css.sectionHeading}>
                    <div>
                      <FormattedMessage id="EditListingPhotosForm.damage1" values={{ required: <span className={css.requiredText}>*Required</span> }} />
                    </div>
                    <span className={css.damageText}>
                      <FormattedMessage id="EditListingPhotosForm.damageError" />
                    </span>
                  </label>
                  <UploadField
                    type={DAMAGE}
                    uploadImagesInProgress={uploadImagesInProgress}
                    uploadOtherImages={uploadOtherImages}
                    form={form}
                    onImageUploader={onImageUploader}
                    labelContent={
                      <span className={css.chooseImageText}>
                        <IconCollection icon="uploadIcon" />
                        <span className={css.chooseImage}>
                          <FormattedMessage id="EditListingPhotosForm.chooseImage1" />
                        </span>
                        <span className={css.imageTypes}>
                          <FormattedMessage id="EditListingPhotosForm.imageTypes1" />
                        </span>
                      </span>
                    }
                    intl={intl}
                    handleRemoveClick={handleRemoveClick}
                  />
                </div>) : null
              }

            </div>
            <ImageUploadError
              uploadOverLimit={uploadOverLimit}
              uploadImageError={uploadImageError}
            />
            <PublishListingError error={publishListingError} />
            <ShowListingsError error={showListingsError} />
            {((uploadImages || listingImagesUrl)?.length < 3 || (uploadImages || listingImagesUrl)?.length > 10 || disableCheck) && (
              <span className={css.error}>
                <FormattedMessage
                  id="EditListingPhotosForm.errorText"
                  values={{ TabName: <b>Photos</b> }}
                />
              </span>
            )}
            <div className={css.buttonContainer}>
              <Button
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled || ((uploadImages || listingImagesUrl)?.length < 3 || (uploadImages || listingImagesUrl)?.length > 10)}
                ready={submitReady}
              >
                {saveActionMsg}
              </Button>

              <Button
                className={css.draftButton}
                type="button"
                disabled={submitDisabled || ((uploadImages || listingImagesUrl)?.length < 3 || (uploadImages || listingImagesUrl)?.length > 10)}
                inProgress={saveAsDraft}
                onClick={() => {
                  setSaveAsDraft(true)
                  const {
                    addImage,
                    heroImg,
                    listingImagesUrl,
                    otherImages,
                    listingVideo,
                    ...updateValues
                  } = values;

                  // Determine hero image ID
                  const heroImageId =
                    heroImg ||
                    listingImagesUrl?.[0]?.public_id ||
                    uploadImages?.[0]?.public_id ||
                    null;

                  // Construct the listing video object
                  const listingVideoObj = listingVideo?.url
                    ? {
                      url: listingVideo.url,
                      public_id: listingVideo.public_id,
                      resource_type: listingVideo.resource_type,
                    }
                    : uploadVideo?.url
                      ? {
                        url: uploadVideo.url,
                        public_id: uploadVideo.public_id,
                        resource_type: uploadVideo.resource_type,
                      }
                      : null;

                  // Compose publicData
                  updateValues.publicData = {
                    listingImagesUrl: (uploadImages?.length > 0) ? uploadImages : listingImagesUrl || [],
                    heroImageId,
                    updatedAt: Math.floor(Date.now() / 1000),
                    listingVideo: listingVideoObj,
                    otherImages: (uploadOtherImages?.length > 0) ? uploadOtherImages : (otherImages.length > 0) ? otherImages : null,
                    version: mainProductType ? 2 : null,
                  };

                  // Submit the data and handle post-submission cleanups
                  onSaveDraft(updateValues).then(() => {
                    history.push('/listings')
                  });
                }}
              >
                <FormattedMessage id="EditListingsDetailsForm.saveAsDraft" />
              </Button>
            </div>
          </Form>
        );
      }}
    />
  );
};

EditListingPhotosFormComponent.defaultProps = { fetchErrors: null };

EditListingPhotosFormComponent.propTypes = {
  fetchErrors: shape({
    publishListingError: propTypes.error,
    showListingsError: propTypes.error,
    uploadImageError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  intl: intlShape.isRequired,
  onImageUpload: func.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  onRemoveImage: func.isRequired,
  listingImageConfig: object.isRequired,
};

export default compose(injectIntl)(EditListingPhotosFormComponent);
